import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { PageLoading } from './components/page-loading';
import { createQueryClient } from './lib/query-client';
import { routeTree } from './routeTree.gen';

/** What every route sees in `context`; the guards are the reason it is not empty. */
export type RouterContext = {
  readonly queryClient: QueryClient;
};

/**
 * Builds the router and the query cache it reads through.
 *
 * One owner for both: the guards call `queryClient.ensureQueryData` during `beforeLoad`,
 * so a router without a query client could not protect anything. `defaultPreload: 'intent'`
 * warms a route on hover, and `defaultPreloadStaleTime: 0` hands freshness decisions to
 * react-query instead of letting the router cache a second copy.
 */
export const createRouter = () => {
  const queryClient = createQueryClient();
  const router = createTanStackRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    /*
     * 刷新页面时数据要等 API，等的时候不能是白屏。没有 pendingComponent 时路由什么都不渲染
     * （match 处于 pending，组件返回 null），这正是「刷新没有 loading」的原因。
     *
     * 两个时间是一组：`defaultPendingMs` 是“多久之后才开始显示”，`defaultPendingMinMs`
     * 是“显示之后至少停留多久”——否则慢一点点的时候 loading 一闪而过，比不显示更像故障。
     * 200/400 是给**页面内导航**定的：路由组件是懒加载的，点菜单通常几十毫秒就绪，200ms
     * 以内不打扰；真慢了就先给反馈。刷新的第一段不归这两个数字管：那时 React 还没渲染任何
     * 东西，界面由 index.html 的 #boot 顶着，React 一渲染出内容（占位或页面）它就退场。
     */
    defaultPendingComponent: PageLoading,
    defaultPendingMs: 200,
    defaultPendingMinMs: 400,
  });
  return router;
};

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}
